/**
 * Coordinate transformation utilities
 * 
 * Normalized coordinates: values in [0, 1] relative to page dimensions
 * Pixel coordinates: actual screen pixels accounting for scale and rotation
 */

/**
 * Convert normalized rect to pixel rect
 * @param {Object} normRect - { x, y, w, h } in [0, 1]
 * @param {Object} pageMetrics - { width, height, rotation } from PDF
 * @param {number} scale - current zoom scale
 * @returns {Object} - { left, top, width, height } in pixels
 */
export function toPxRect(normRect, pageMetrics, scale) {
  const { width: pageWidth, height: pageHeight, rotation = 0 } = pageMetrics;
  
  // Base pixel dimensions
  const pxWidth = pageWidth * scale;
  const pxHeight = pageHeight * scale;
  
  // Round to nearest pixel for precise positioning
  const roundToPx = (val) => Math.round(val * 100) / 100;
  
  // For rotation 0 or 180, straightforward
  if (rotation === 0) {
    return {
      left: roundToPx(normRect.x * pxWidth),
      top: roundToPx(normRect.y * pxHeight),
      width: roundToPx(normRect.w * pxWidth),
      height: roundToPx(normRect.h * pxHeight),
    };
  }
  
  // For 90/270, dimensions swap
  if (rotation === 90 || rotation === 270) {
    // Swap and adjust based on rotation
    // This is simplified; for production, use PDF.js viewport.convertToViewportRectangle
    return {
      left: roundToPx(normRect.x * pxHeight),
      top: roundToPx(normRect.y * pxWidth),
      width: roundToPx(normRect.w * pxHeight),
      height: roundToPx(normRect.h * pxWidth),
    };
  }
  
  // Default fallback
  return {
    left: roundToPx(normRect.x * pxWidth),
    top: roundToPx(normRect.y * pxHeight),
    width: roundToPx(normRect.w * pxWidth),
    height: roundToPx(normRect.h * pxWidth),
  };
}

/**
 * Convert pixel coordinates to normalized rect
 * @param {Object} pxRect - { left, top, width, height } in pixels
 * @param {Object} pageMetrics - { width, height, rotation }
 * @param {number} scale - current zoom scale
 * @returns {Object} - { x, y, w, h } in [0, 1]
 */
export function toNormRect(pxRect, pageMetrics, scale) {
  const { width: pageWidth, height: pageHeight, rotation = 0 } = pageMetrics;
  
  const pxWidth = pageWidth * scale;
  const pxHeight = pageHeight * scale;
  
  if (rotation === 0) {
    return {
      x: pxRect.left / pxWidth,
      y: pxRect.top / pxHeight,
      w: pxRect.width / pxWidth,
      h: pxRect.height / pxHeight,
    };
  }
  
  // For rotations, adjust accordingly
  if (rotation === 90 || rotation === 270) {
    return {
      x: pxRect.left / pxHeight,
      y: pxRect.top / pxWidth,
      w: pxRect.width / pxHeight,
      h: pxRect.height / pxWidth,
    };
  }
  
  return {
    x: pxRect.left / pxWidth,
    y: pxRect.top / pxHeight,
    w: pxRect.width / pxWidth,
    h: pxRect.height / pxHeight,
  };
}

/**
 * Clamp normalized rect to stay within page bounds
 */
export function clampNormRect(normRect, minW = 0.02, minH = 0.02) {
  const x = Math.max(0, Math.min(1 - normRect.w, normRect.x));
  const y = Math.max(0, Math.min(1 - normRect.h, normRect.y));
  const w = Math.max(minW, Math.min(1 - x, normRect.w));
  const h = Math.max(minH, Math.min(1 - y, normRect.h));
  
  return { x, y, w, h };
}
